import React, { createContext, useCallback, useContext, useState } from "react";

type ToastType = "success" | "error" | "info";

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} });

let _id = 0;

// Callers sometimes pass a raw API error payload (e.g. `err.response.data.detail`, which
// FastAPI can return as a string, but also as an array of {msg} validation-error objects,
// or an object with a `.message`). Coerce anything non-string here so a mistyped call site
// degrades to readable text instead of crashing the whole app on React error #31.
function coerceMessage(message: unknown): string {
  if (typeof message === "string") return message;
  if (Array.isArray(message)) {
    const msgs = message.map(m => (typeof (m as any)?.msg === "string" ? (m as any).msg : null)).filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  } else if (message && typeof message === "object" && typeof (message as any).message === "string") {
    return (message as any).message;
  }
  return "Something went wrong — please try again";
}

export function ToastProvider({ children }: React.PropsWithChildren<{}>) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, type: ToastType = "success") => {
    const id = ++_id;
    setToasts(prev => [...prev, { id, message: coerceMessage(message), type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  const COLORS: Record<ToastType, { bg: string; border: string; icon: string }> = {
    success: { bg: "#f0fdf4", border: "#86efac", icon: "#16a34a" },
    error:   { bg: "#fef2f2", border: "#fca5a5", icon: "#dc2626" },
    info:    { bg: "#eff6ff", border: "#93c5fd", icon: "#2563eb" },
  };

  const ICONS: Record<ToastType, React.ReactNode> = {
    success: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
      </svg>
    ),
    error: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
      </svg>
    ),
    info: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/*
        Two live regions, not one, and the toasts themselves carry no live role.

        Toasts are how this app reports the outcome of every save, delete and
        submit, and they were previously announced to nobody: no role, no
        aria-live. Adding both a live region AND a live role on each toast
        inside it would have overcorrected — nested live regions make several
        screen readers announce the same message twice.

        So the announcement lives on the containers, which is also the more
        reliable half of the pattern: a live region must already be in the DOM
        when content is inserted into it for the insertion to be announced, and
        these two mount with the provider and never unmount.

        Split by urgency because a failure deserves to interrupt and a success
        does not: assertive cuts into whatever is being read, polite waits for a
        pause. One region cannot do both.

        data-testid is the stable hook for tests. `toast-slide-in` is an
        animation class and would be the wrong thing to select on.
      */}
      {/*
        One positioned wrapper, unchanged, holding the two live regions as
        ordinary flex children — so the toasts stack top-right exactly as
        before. Errors group above the rest, matching the order they are
        announced in; that is the only visible difference.
      */}
      <div
        data-testid="toast-region"
        style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          display: "flex", flexDirection: "column", gap: 8,
          pointerEvents: "none",
        }}
      >
        {(["assertive", "polite"] as const).map(politeness => (
          <div
            key={politeness}
            role={politeness === "assertive" ? "alert" : "status"}
            aria-live={politeness}
            aria-label={politeness === "assertive" ? "Errors" : "Notifications"}
            data-testid={`toast-region-${politeness}`}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {toasts
              .filter(t => (politeness === "assertive" ? t.type === "error" : t.type !== "error"))
              .map(t => {
                const c = COLORS[t.type];
                return (
                  <div
                    key={t.id}
                    // No live role here on purpose — the region above is the
                    // live region, and nesting one inside another makes several
                    // screen readers announce the same message twice.
                    data-testid="toast"
                    data-toast-type={t.type}
                    className="toast-slide-in"
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      background: c.bg,
                      border: `1px solid ${c.border}`,
                      borderRadius: 10,
                      padding: "11px 16px",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                      fontSize: 13.5, fontWeight: 500,
                      color: "#1e293b",
                      minWidth: 260, maxWidth: 380,
                      pointerEvents: "all",
                    }}
                  >
                    <span style={{ color: c.icon, flexShrink: 0 }}>{ICONS[t.type]}</span>
                    {t.message}
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
